import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { ChevronDown, ChevronUp, Calendar, FileText, Edit, Trash2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PastSymptoms = ({ symptoms, patientId, onEdit, onDelete }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showCount, setShowCount] = useState(5);
  const [notes, setNotes] = useState({});
  
  // Filter out empty symptom entries
  const filteredSymptoms = symptoms.filter(s => s.symptoms && s.symptoms.length > 0);

  useEffect(() => {
    if (patientId) {
      fetchNotes();
    }
  }, [patientId]);

  const fetchNotes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/notes/${patientId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const notesMap = {};
      response.data.forEach(note => {
        notesMap[note.date] = note.content;
      });
      setNotes(notesMap);
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  };

  // Sort filtered symptoms by date (most recent first)
  const sortedSymptoms = [...filteredSymptoms].sort((a, b) => new Date(b.date) - new Date(a.date));
  const displayedSymptoms = isExpanded ? sortedSymptoms : sortedSymptoms.slice(0, showCount);

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'MMM dd, yyyy');
    } catch {
      return dateStr;
    }
  };

  if (filteredSymptoms.length === 0) {
    return (
      <div className="card" data-testid="past-symptoms">
        <h2 className="text-xl font-semibold mb-4" style={{ color: '#A2DAD3' }}>Symptom History</h2>
        <p className="text-gray-500 text-center py-8">No symptoms recorded yet</p>
      </div>
    );
  }

  return (
    <div className="card" data-testid="past-symptoms">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold flex items-center gap-2" style={{ color: '#A2DAD3' }}>
          <Calendar size={20} />
          Symptom History
        </h2>
        <span className="text-sm text-gray-600">{filteredSymptoms.length} entries</span>
      </div>

      <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
        <AnimatePresence>
          {displayedSymptoms.map((symptom, index) => (
            <motion.div
              key={symptom.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ delay: index * 0.05 }}
              className="p-4 rounded-xl"
              style={{ backgroundColor: '#f0f9f8', border: '1px solid #e0f0ee' }}
              data-testid={`symptom-entry-${symptom.id}`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <p className="font-medium" style={{ color: '#333' }}>{formatDate(symptom.date)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: '#A2DAD3', color: 'white' }}>
                    {symptom.symptoms.length} symptoms
                  </span>
                  {onEdit && (
                    <button
                      onClick={() => onEdit(symptom)}
                      className="text-blue-500 hover:text-blue-700 p-1"
                      title="Edit symptom"
                    >
                      <Edit size={14} />
                    </button>
                  )}
                  <button
                    onClick={async () => {
                      if (!window.confirm('Delete this symptom entry?')) return;
                      try {
                        const token = localStorage.getItem('token');
                        await axios.delete(`${API}/symptoms/${symptom.id}`, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        toast.success('Symptom deleted');
                        if (onDelete) onDelete();
                      } catch (error) {
                        toast.error('Failed to delete symptom');
                      }
                    }}
                    className="text-red-500 hover:text-red-700 p-1"
                    title="Delete symptom"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mb-2">
                {symptom.symptoms.map((s, i) => (
                  <span
                    key={i}
                    className="text-sm px-3 py-1 rounded-full"
                    style={{ backgroundColor: 'white', border: '1px solid #A2DAD3', color: '#333' }}
                  >
                    {s}
                  </span>
                ))}
              </div>
              {symptom.notes && (
                <p className="text-sm text-gray-600 mt-2 italic">{symptom.notes}</p>
              )}
              {notes[symptom.date] && (
                <div className="mt-3 p-3 rounded-lg" style={{ backgroundColor: '#fff5f5', border: '1px solid #F9D6A7' }}>
                  <div className="flex items-center gap-2 mb-1">
                    <FileText size={14} style={{ color: '#F9D6A7' }} />
                    <span className="text-xs font-medium" style={{ color: '#F9D6A7' }}>Note:</span>
                  </div>
                  <p className="text-sm text-gray-700">{notes[symptom.date]}</p>
                </div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {filteredSymptoms.length > showCount && (
        <button
          data-testid="toggle-symptoms-button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full mt-4 p-2 rounded-lg flex items-center justify-center gap-2 text-sm font-medium"
          style={{ backgroundColor: '#f0f9f8', color: '#A2DAD3' }}
        >
          {isExpanded ? (
            <>
              Show Less <ChevronUp size={16} />
            </>
          ) : (
            <>
              Show All ({symptoms.length - showCount} more) <ChevronDown size={16} />
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default PastSymptoms;
